import type { FeatureFlagWithState } from '@viapeople/api'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

export type { FeatureFlagWithState } from '@viapeople/api'

interface FeatureFlagsState {
	flags: Record<string, boolean> // key -> enabled
	flagsList: FeatureFlagWithState[]
	lastUpdated: Date | null
	isLoading: boolean
	error: string | null

	// Actions
	setFlags: (flags: FeatureFlagWithState[]) => void
	isEnabled: (key: string) => boolean
	getFlag: (key: string) => FeatureFlagWithState | undefined
	clearFlags: () => void
	setLoading: (loading: boolean) => void
	setError: (error: string | null) => void
}

export const useFeatureFlagsStore = create<FeatureFlagsState>()(
	devtools(
		(set, get) => ({
			flags: {},
			flagsList: [],
			lastUpdated: null,
			isLoading: false,
			error: null,

			setFlags: (flags) => {
				const flagsMap = flags.reduce(
					(acc, flag) => {
						acc[flag.key] = flag.enabled
						return acc
					},
					{} as Record<string, boolean>,
				)

				set({
					flags: flagsMap,
					flagsList: flags,
					lastUpdated: new Date(),
					error: null,
				})
			},

			isEnabled: (key) => {
				return get().flags[key] ?? false
			},

			getFlag: (key) => {
				return get().flagsList.find((flag) => flag.key === key)
			},

			clearFlags: () => {
				set({
					flags: {},
					flagsList: [],
					lastUpdated: null,
				})
			},

			setLoading: (loading) => {
				set({ isLoading: loading })
			},

			setError: (error) => {
				set({ error })
			},
		}),
		{ name: 'feature-flags-store' },
	),
)
